import { z } from 'zod';
/**
 * Bulk import — the request and report shapes.
 *
 * ⚠️ **The target fleet account comes from CONTEXT, and since phase 4 a row MAY NAME ONE**
 * (A1 v2 / Q1). What that means depends on where the caller is standing, and the asymmetry is
 * the whole rule: inside a depot the column may only CONFIRM where you already are, because a
 * CSV must not be a way out of the tenant your guard admitted you to; from the COMPANY it is
 * REQUIRED, because a company holds no vehicles itself (D2) and a blank there is not "here",
 * it is nowhere.
 *
 * This is what §0.1's two partial unique indexes were for — `20260921000027` made a fleet
 * account nameable, and naming one is what lets a partner upload across several depots at once.
 */
/** How many rows one file may carry. */
export const VEHICLE_IMPORT_MAX_ROWS = 5000;
/**
 * The six things a row can be (D3).
 *
 * ⚠️ **ONE file with a `level` column, never six files.** Six means six uploads, six orderings
 * to get right, and a partner who uploads companies before resellers meets a wall of "reseller
 * not found" for a file that is perfectly correct. It is also what a customer can actually
 * produce from a spreadsheet — measured on the first real one brought to this feature, which
 * carried all six levels in one sheet.
 *
 * ⚠️ **No surface creates all six.** Each declares what it may create and refuses the rest BY
 * NAME (D8a); the levels are the vocabulary, not a permission.
 */
export const IMPORT_LEVELS = [
    'PARTNER',
    'RESELLER',
    'COMPANY',
    'FLEET_ACCOUNT',
    'VEHICLE',
    'DRIVER',
];
export const importLevelSchema = z.enum(IMPORT_LEVELS);
/**
 * The order rows are APPLIED in, regardless of their order in the file (D3).
 *
 * ⚠️ **A combined file must create the branch before the lorries that sit in it**, so a
 * spreadsheet written in any order works. Within a level file order is preserved, so the report
 * reads alongside the spreadsheet the operator is looking at.
 *
 * ⚠️ `VEHICLE` and `DRIVER` share the last rank deliberately — neither references the other.
 * Linking a driver to a vehicle is NOT in this file (A8): `VehicleTelemetry.driverId` is
 * resolved from the tacho card at ingestion, so ordering them relative to each other would
 * imply a relationship the platform does not hold.
 */
export const IMPORT_LEVEL_ORDER = {
    PARTNER: 0,
    RESELLER: 1,
    COMPANY: 2,
    FLEET_ACCOUNT: 3,
    VEHICLE: 4,
    DRIVER: 4,
};
/**
 * A human word for a level, for a refusal or a report.
 *
 * ⚠️ Lower case and singular: these appear mid-sentence ("a row at level …"), and a
 * SCREAMING_SNAKE value in prose is the platform's vocabulary leaking onto a page an operations
 * manager reads — the `GATED_RELIABILITY` lesson. The ENUM is what the API takes and what a
 * refusal quotes back, so both are available.
 */
export const IMPORT_LEVEL_LABELS = {
    PARTNER: 'partner',
    RESELLER: 'reseller',
    COMPANY: 'company',
    FLEET_ACCOUNT: 'fleet account',
    VEHICLE: 'vehicle',
    DRIVER: 'driver',
};
/**
 * The columns a vehicle import file may carry, in the order a template writes them.
 *
 * ⚠️ **This is a GUARDED COPY of `VEHICLE_COLUMNS` in `@oip/domain`, and the copy is
 * unavoidable.** The parser owns the vocabulary and is pure, but `@oip/domain` is CommonJS
 * and the web app takes its types and not its runtime values (R9) — so a screen that must
 * name the columns cannot import the original. The same seam `POLICY_EVENT_TYPES` sits on,
 * with the same mitigation: `apps/api/test/vehicle-import-columns.test.ts` imports BOTH
 * packages and fails on any difference, including ORDER, because the order is what a
 * template writes and an operator matches by eye.
 *
 * The alternative — a `GET` returning the template — was rejected: it is a network round
 * trip for a constant, and it would leave the screen unable to label a column until it
 * had loaded.
 */
export const VEHICLE_IMPORT_COLUMNS = [
    'name',
    'registration',
    'unique_id',
    'provider_device_id',
    'vehicle_class',
    'drivetrain',
    'drivers_hours_ruleset',
];
/**
 * What a `DRIVER` row carries (A8).
 *
 * ⚠️ **`name` is SHARED with the vehicle columns and that is deliberate** — one header serves
 * both levels, which is what makes a combined file a spreadsheet rather than a union of two.
 *
 * ⚠️ **There is no `vehicle` column here, and its absence is a decision** (A8). Linking a driver
 * to a vehicle is not in this file: `VehicleTelemetry.driverId` is resolved from the tacho card
 * at ingestion, and a default-driver assignment is a temporal relationship that does not exist
 * on this platform. A column would imply one and store nothing.
 */
export const DRIVER_IMPORT_COLUMNS = ['name', 'licence_no', 'tacho_card_id'];
export const DRIVER_IMPORT_REQUIRED_COLUMNS = ['name'];
/**
 * ⚠️ `licence_no` is OPTIONAL and that is not an oversight — `createDriverRequestSchema` makes
 * it optional too, so demanding it here would refuse a file the driver dialog would accept.
 *
 * The consequence is stated rather than left to be found: a driver with no licence number is
 * **not idempotent on re-run** (A2/D6 rest on `(tenantId, licenceNo)`), so re-uploading the
 * same file creates them a second time. The dry run says so per row rather than refusing,
 * because a fleet that has not collected licence numbers still needs its drivers on the
 * platform.
 */
export const DRIVER_IMPORT_COLUMN_HELP = {
    name: 'Required. The driver’s name, as it should appear on screen.',
    licence_no: 'Optional but strongly advised. Identifies the driver on a re-run — without it, importing ' +
        'the same file twice creates them twice. Reserved even after a driver is archived.',
    tacho_card_id: 'Optional. The tachograph card. This is what attributes drivers’ hours to a person at ' +
        'ingestion, so a driver without one reports no hours. Released when a driver is archived, ' +
        'because a card is reissued.',
};
/**
 * The two columns a row cannot omit.
 *
 * ⚠️ **The importer READS this rather than declaring its own**, so there is exactly one
 * statement of what is required and the screen cannot come to disagree with the server
 * about it. Everything else is optional because the create is — a class, a ruleset, a
 * drivetrain and both customer identifiers are all legitimately absent, and demanding one
 * here would refuse a file the vehicle dialog would accept.
 */
export const VEHICLE_IMPORT_REQUIRED_COLUMNS = ['name', 'registration'];
/**
 * One line per column, for the screen.
 *
 * ⚠️ `Record<VehicleImportColumn, string>` deliberately, so a column added to the
 * vocabulary without help is a COMPILE error and a stale entry for a column that has gone
 * is too — the `categoryIcons.tsx` rule, which is why neither needs a parity test.
 *
 * ⚠️ **No entry enumerates the allowed DRIVETRAIN values.** They are `DRIVETRAINS`, which
 * the screen can read directly, and a list repeated in prose is a second copy that drifts
 * the first time somebody adds a powertrain.
 *
 * ⚠️ The wording avoids *registration* / *license plate* — the word this product renames
 * per market — and says **plate**, which both markets read. A help string that took a
 * locale would make this a function, and the COLUMN NAME is a file format rather than
 * display text: it is `registration` for every reader, in every market.
 */
export const VEHICLE_IMPORT_COLUMN_HELP = {
    name: 'Required. What the vehicle is called on screen.',
    registration: 'Required. The plate painted on the vehicle. Must be unique in this file and unused here — ' +
        'including by an archived vehicle, whose plate stays reserved.',
    unique_id: 'Optional. Your own identifier — an asset number, a serial, or the telematics unit’s IMEI. ' +
        'Must be unique in this file and unused here.',
    provider_device_id: 'Optional. What your telematics provider keys this vehicle by. Must be unique in this file, ' +
        'and unused by a vehicle that is still active.',
    vehicle_class: 'Optional. Must already exist — the import creates no classes.',
    drivetrain: 'Optional. Blank means unknown, never diesel.',
    drivers_hours_ruleset: 'Optional. Must already exist. Blank inherits the fleet’s limits.',
};
/**
 * A template file: the header row and nothing else.
 *
 * ⚠️ **No example row, and that is a decision rather than laziness.** An example row the
 * operator forgets to delete becomes a REAL vehicle — and under D3 its plate is then
 * reserved for ever, so the mistake is not fully reversible by archiving what it created.
 * A header row cannot do that, and the screen carries the per-column help and the allowed
 * drivetrain values, which is what an example row was for.
 */
export function vehicleImportTemplate() {
    return `${VEHICLE_IMPORT_COLUMNS.join(',')}\n`;
}
/**
 * What a hierarchy row carries (Part B).
 *
 * ⚠️ **`name` is shared with the vehicle and driver levels**, exactly as they share it with
 * each other — one header serves every level, which is what makes a combined file a
 * spreadsheet rather than a union of six.
 *
 * ⚠️ The PATH columns are separate (`PATH_IMPORT_COLUMNS`) because they address a row rather
 * than describe it, and every level is addressed the same way.
 */
export const HIERARCHY_IMPORT_COLUMNS = [
    'name',
    'region',
    'admin_email',
    'admin_display_name',
    'policy_template',
];
/**
 * The four path segments (D2).
 *
 * ⚠️ **Every one is optional on the wire, and which are REQUIRED depends on the caller.** D8's
 * rule is a loop rather than three hand-written cases: *for every path segment ABOVE the
 * caller's level, substitute the session's value*. A partner-scoped caller's `partner_code` is
 * dropped; a reseller-scoped caller's `reseller_name` is dropped; a company administrator's
 * `company_name` is dropped — and that last one is the tier it is easiest to forget, which is
 * how a CSV becomes a way to file a branch under somebody else's customer.
 */
export const PATH_IMPORT_COLUMNS = [
    'partner_code',
    'reseller_name',
    'company_name',
    'fleet_account_name',
];
/**
 * Which path segment ADDRESSES each level — the segment a row of that level FILLS IN rather
 * than one it points through.
 *
 * ⚠️ Declared rather than derived from the order, because the mapping is what makes D8's loop
 * expressible: everything ABOVE a row's own segment is an ancestor and may be substituted;
 * the segment itself is the row's name and may not.
 */
export const LEVEL_PATH_COLUMN = {
    PARTNER: 'partner_code',
    RESELLER: 'reseller_name',
    COMPANY: 'company_name',
    FLEET_ACCOUNT: 'fleet_account_name',
};
/**
 * The PATH columns — which fleet account a row is aimed at (A1 v2 / Q1).
 *
 * ⚠️ **Optional, and what it means depends on where you are standing.** Inside a depot it may
 * only confirm where you already are; from the COMPANY it is REQUIRED, because a company holds
 * no vehicles itself and a blank there is not "here", it is nowhere.
 *
 * ⚠️ Separate from the level columns rather than folded into them, because it applies to every
 * level equally — a vehicle, a driver and (in phase 6) a fleet account are all addressed the
 * same way, and repeating it per level is how the copies start.
 */
export const PATH_IMPORT_COLUMN_HELP = {
    /*
     * ⚠️ All four, because three of them rendered as a BARE COLUMN NAME with no description on
     * the platform screen — the help had only ever been written for the one surface that
     * existed. Reported from the built page.
     */
    partner_code: 'The partner this row belongs under. Fixed from where you are standing on every surface ' +
        'except the platform’s — a file cannot move a customer to a different partner.',
    reseller_name: 'The reseller this row belongs under. Fixed from where you are standing when you ' +
        'administer one reseller, so a file cannot file a customer under a sibling’s book.',
    company_name: 'The customer this row belongs under. Fixed from where you are standing inside a company.',
    fleet_account_name: 'Optional inside a depot, REQUIRED when importing from the customer. Names the depot the ' +
        'row belongs to. Inside a depot it may only name the one you are in — a file cannot send ' +
        'rows somewhere you are not standing.',
};
/**
 * Which columns a level reads.
 *
 * ⚠️ Declared rather than derived, because the overlap is the interesting part: `name` is read
 * by both levels and means a different thing in each, and a template built by concatenating two
 * lists would emit it twice.
 */
/**
 * What each HIERARCHY column means.
 *
 * ⚠️ **There was none of this**, because the screen that needed it did not exist — the four
 * hierarchy levels had columns and no wording, so the import page went on describing the
 * VEHICLE file at every level. A reader on the platform screen was told to write
 * `VEHICLE or DRIVER` and would have had every row refused.
 */
export const HIERARCHY_IMPORT_COLUMN_HELP = {
    /*
     * ⚠️ **The caveats are gone because the STRUCTURE carries them now.** These sentences said
     * "companies only" and "every level except a reseller" because every level was shown every
     * column; with `COLUMNS_FOR_LEVEL` narrowed, a column appears only where it applies and the
     * prose would be restating the heading above it.
     */
    name: 'The partner’s name, and the brand it trades under.',
    region: 'Where the data is held. Leave it out and the customer takes the platform default.',
    admin_email: 'Required. The first administrator, who is emailed an invitation as soon as the record is ' +
        'made — so a wrong address here reaches a real person.',
    admin_display_name: 'Optional. Falls back to the part of the address before the @.',
    policy_template: 'Not yet read by the import. A company takes the default set of decision policies; ' +
        'choose a different one on the company itself.',
};
/**
 * The COLUMNS a surface's file may carry, derived once.
 *
 * ⚠️ **Shared with `importTemplate` deliberately.** The template and the on-screen help are
 * two renderings of ONE answer, and this codebase has paid repeatedly for letting such a pair
 * drift — two copies of `inferActiveTenant`, the per-channel transport map, `Can`'s private
 * `hasRole`. Help describing different columns from the template beside it is worse than no
 * help: a reader builds a file from it and every row is refused.
 */
export function importColumnsFor(levels) {
    /*
     * ⚠️ The segments this surface FILLS IN, plus the one that LOCATES its deepest rows.
     *
     * ⚠️ **It used to be "every segment down to the deepest", which emitted all four
     * everywhere** — contradicting this function's own comment that *"a platform file needs
     * `partner_code` and a depot file does not"*. Harmless on the wire, because an ancestor
     * column above the caller is DROPPED rather than refused (D8), and misleading on screen:
     * a depot manager was shown `partner_code` on a file their surface fixes from the session.
     *
     * An ancestor ABOVE the caller comes from the session, never the file, so it belongs in
     * neither the template nor the help. `fleet_account_name` survives in every template
     * regardless — vehicles and drivers are ADDRESSED by it rather than filling it in, which is
     * why they have no `LEVEL_PATH_COLUMN` entry of their own.
     */
    const fills = new Set(levels.map((l) => LEVEL_PATH_COLUMN[l]).filter((c) => c !== undefined));
    if (levels.some((l) => LEVEL_PATH_COLUMN[l] === undefined))
        fills.add('fleet_account_name');
    const path = PATH_IMPORT_COLUMNS.filter((column) => fills.has(column));
    /*
     * ⚠️ **Grouped by WHICH LEVELS USE A COLUMN, not listed per level.** Per level, the platform
     * screen printed the same five columns four times — twenty entries for five facts — and a
     * reader scrolling it cannot tell which differences are real. Reported from the built page.
     *
     * Columns sharing an applicability set become one group, so a surface whose levels happen to
     * share everything collapses to a single list, and one whose levels are disjoint (vehicles
     * and drivers) still reads as two. The grouping is DERIVED, so it follows the catalogue
     * rather than a rule somebody has to maintain.
     */
    const usedBy = new Map();
    const order = [];
    for (const level of levels) {
        for (const column of COLUMNS_FOR_LEVEL[level] ?? []) {
            if (!usedBy.has(column)) {
                usedBy.set(column, []);
                order.push(column);
            }
            usedBy.get(column).push(level);
        }
    }
    const groups = [];
    for (const column of order) {
        const on = usedBy.get(column);
        const key = on.join('|');
        const existing = groups.find((g) => g.levels.join('|') === key);
        if (existing)
            existing.columns.push(column);
        else
            groups.push({ levels: on, columns: [column] });
    }
    return { path, groups };
}
export const COLUMNS_FOR_LEVEL = {
    VEHICLE: VEHICLE_IMPORT_COLUMNS,
    DRIVER: DRIVER_IMPORT_COLUMNS,
    /*
     * ⚠️ **What each level ACTUALLY reads, read off the create dispatch rather than assumed.**
     * All four used to declare the whole hierarchy set, which put the same five columns on
     * screen four times over — and the shared wording was then WRONG in three of the four
     * places: *"Optional, and companies only"* under PARTNER ROWS, *"required for every level
     * except a reseller"* under RESELLER ROWS.
     *
     * ⚠️ A RESELLER reads NOTHING beyond its path segment — it is named by `reseller_name` and
     * mints no administrator, which is exactly why it is the one level the deliverability gate
     * lets through unconditionally.
     *
     * ⚠️ `name` is PARTNER-only: every other level takes its name from its own path segment.
     * ⚠️ `region` is COMPANY-only.
     * ⚠️ `policy_template` is in NO list, because NOTHING READS IT — the create dispatch takes a
     *    `policyTemplateId` (a uuid) and this column is a name, so it is recognised and silently
     *    ignored. It stays in `HIERARCHY_IMPORT_COLUMNS` so a file carrying it is not refused
     *    wholesale (D8), and out of here so the screen does not document a column that does
     *    nothing.
     */
    PARTNER: ['name', 'admin_email', 'admin_display_name'],
    RESELLER: [],
    COMPANY: ['region', 'admin_email', 'admin_display_name'],
    FLEET_ACCOUNT: ['admin_email', 'admin_display_name'],
};
/**
 * EVERY column any level reads, plus the path and `level` itself.
 *
 * ⚠️ **What a surface RECOGNISES is not what it CREATES**, and conflating the two refuses the
 * combined file this whole design rests on. Measured on a customer's own file: the hierarchy
 * surface read `registration` as an unknown column and refused all thirteen rows — including
 * the five it was perfectly able to create — because the file also carried vehicles.
 *
 * D8 already states the principle for the PATH columns: *"dropped rather than refused, because
 * the column is legitimate in the platform's own file and the same spreadsheet should survive
 * being handed up and down the chain."* A level's own columns are legitimate for exactly the
 * same reason, and a file is uploaded to each surface in turn precisely so each creates what
 * it may.
 */
export const ALL_IMPORT_COLUMNS = [
    'level',
    ...PATH_IMPORT_COLUMNS,
    ...new Set([...VEHICLE_IMPORT_COLUMNS, ...DRIVER_IMPORT_COLUMNS, ...HIERARCHY_IMPORT_COLUMNS]),
];
/**
 * `partner` / `partners`, `company` / `companies`.
 *
 * ⚠️ A helper rather than `${label}s`, which produced **"companys"** in a live refusal. English
 * plurals are not a suffix, and the one level whose plural is irregular is the one a partner
 * reads most often.
 */
export const IMPORT_LEVEL_PLURALS = {
    PARTNER: 'partners',
    RESELLER: 'resellers',
    COMPANY: 'companies',
    FLEET_ACCOUNT: 'fleet accounts',
    VEHICLE: 'vehicles',
    DRIVER: 'drivers',
};
/**
 * A template for the levels a surface can actually create.
 *
 * ⚠️ **The surface decides, not the catalogue.** Emitting every column of all six levels would
 * hand a fleet administrator a header with `partner_code` and `policy_template` in it — columns
 * their own import would refuse — which is the "offer what the device has, not what the schema
 * has" rule, one feature along.
 *
 * ⚠️ **`level` comes FIRST and is always present**, even for a single-level surface. A file
 * without it is read as that surface's default (which is why the column is optional on the way
 * in), but a template that omitted it would teach every operator to write single-level files
 * and then surprise them the day they need two.
 *
 * ⚠️ Still no example row — an example somebody forgets to delete becomes a real vehicle whose
 * plate is reserved for ever (D3), and now a real driver whose licence is reserved too.
 */
/**
 * A WORKED EXAMPLE of a file for this surface — for the SCREEN, never for the template.
 *
 * ⚠️ **The template stays headers-only and that decision is unchanged**: it is the file an
 * operator edits and uploads, so an example row somebody fails to delete becomes a real
 * record — a vehicle whose plate is reserved for ever (D3), or a partner with an
 * administrator nobody asked for. This is the other half of that trade: shown on screen,
 * where reading it costs nothing and importing it takes a deliberate copy, a paste and a dry
 * run the button will not skip.
 *
 * ⚠️ **Every address is `@example.com`, which is RESERVED (RFC 2606) and can receive no
 * mail.** That is the safeguard that matters here rather than a styling choice: the import
 * now genuinely sends invitations, so an example copied in full and applied must not be able
 * to email a real person. A plausible-looking company domain would.
 *
 * ⚠️ It is emitted in the TEMPLATE'S column order, from the template's own derivation, so the
 * example and the header cannot drift into describing different files — the failure the help
 * text already had once.
 */
export function importExample(levels) {
    const header = importTemplate(levels).trim();
    const columns = header.split(',');
    /*
     * One row per level, in LEVEL order — which is also the order rows are applied, so the
     * example reads as the chain being built up rather than as four unrelated lines.
     */
    const ordered = [...levels].sort((a, b) => IMPORT_LEVEL_ORDER[a] - IMPORT_LEVEL_ORDER[b]);
    const rows = ordered.map((level) => {
        const v = EXAMPLE_VALUES[level] ?? {};
        return columns.map((c) => (c === 'level' ? level : (v[c] ?? ''))).join(',');
    });
    return [header, ...rows].join('\n');
}
/**
 * What each level's example row says.
 *
 * ⚠️ The PATH segments repeat down the chain deliberately — a company row names the partner
 * and reseller above it, and seeing the same values repeat is how a reader learns that the
 * path locates a row rather than creating its ancestors again.
 */
const EXAMPLE_VALUES = {
    PARTNER: {
        partner_code: 'northwind',
        name: 'Northwind Telematics',
        admin_email: 'partner.admin@example.com',
        admin_display_name: 'Dana Whitfield',
    },
    RESELLER: {
        partner_code: 'northwind',
        reseller_name: 'Northern Partners',
    },
    COMPANY: {
        partner_code: 'northwind',
        reseller_name: 'Northern Partners',
        company_name: 'Halewood Transport',
        region: 'UK',
        admin_email: 'company.admin@example.com',
    },
    FLEET_ACCOUNT: {
        partner_code: 'northwind',
        reseller_name: 'Northern Partners',
        company_name: 'Halewood Transport',
        fleet_account_name: 'Liverpool Depot',
        admin_email: 'depot.admin@example.com',
    },
    VEHICLE: {
        fleet_account_name: 'Liverpool Depot',
        name: 'Tipper 12',
        registration: 'AB12 CDE',
        vehicle_class: 'Tipper',
        drivetrain: 'DIESEL',
    },
    DRIVER: {
        fleet_account_name: 'Liverpool Depot',
        name: 'A. Fernandes',
        licence_no: 'FERNA751203AF9EK',
    },
};
export function importTemplate(levels) {
    /*
     * ⚠️ `fleet_account_name` is in EVERY template, because the same spreadsheet should survive
     * being handed up and down the chain — a depot manager's file with the column blank is the
     * same file their company can use with it filled in. Emitting it only for company-level
     * surfaces would make the two files different shapes for no reason the operator can see.
     */
    const seen = new Set(['level']);
    // ONE derivation, shared with the on-screen help — see `importColumnsFor`.
    const { path, groups } = importColumnsFor(levels);
    for (const column of path)
        seen.add(column);
    for (const group of groups)
        for (const column of group.columns)
            seen.add(column);
    return `${[...seen].join(',')}\n`;
}
/**
 * WHICH LEVELS each import surface accepts, and how a row with no `level` is read.
 *
 * ⚠️ **ONE declaration, read by the CONTROLLER and by the SCREEN.** They were two hand-written
 * copies for a day, and the cost showed immediately: the page was parameterised by surface and
 * its HELP was not, so the platform screen told a reader `level` was "VEHICLE or DRIVER" —
 * the column that decides how every row is read, described backwards, on a file where those
 * levels are refused.
 *
 * ⚠️ **`defaultLevel` is the dangerous half.** A wrong `levels` list produces a VISIBLE refusal
 * the reader can act on; a wrong default silently reads every row of a single-level file as
 * something else, and the report is internally consistent about it.
 *
 * ⚠️ This is NOT an authorization decision. It says what a FILE may contain; the guards decide
 * what the caller may create (D8a), and a surface listed here still refuses everything its
 * guard refuses.
 */
export const IMPORT_SURFACE_LEVELS = {
    VEHICLE: { levels: ['VEHICLE', 'DRIVER'], defaultLevel: 'VEHICLE' },
    TENANT: { levels: ['FLEET_ACCOUNT'], defaultLevel: 'FLEET_ACCOUNT' },
    PARTNER: { levels: ['RESELLER', 'COMPANY', 'FLEET_ACCOUNT'], defaultLevel: 'COMPANY' },
    PLATFORM: {
        levels: ['PARTNER', 'RESELLER', 'COMPANY', 'FLEET_ACCOUNT'],
        defaultLevel: 'COMPANY',
    },
};
/**
 * What the platform would DO with a row.
 *
 * ⚠️ `SKIP` and `REFUSE` are not the same answer and must never be merged. A
 * plate already held by an ACTIVE vehicle is a re-run of a file that already
 * worked (D6) and is fine; a plate held by an ARCHIVED one is D3 — the number
 * stays reserved because its incidents and trips still carry it — and the
 * reader must be told to RESTORE rather than have the row quietly passed over.
 */
export const importOutcomeSchema = z.enum(['CREATE', 'SKIP', 'REFUSE']);
export const importRowReportSchema = z.object({
    /** 1-based, and the LINE in the file — it is what the operator edits. */
    line: z.number().int(),
    /**
     * What this row was read AS.
     *
     * ⚠️ Present even on a refusal, and especially then: *"a row at level PARTNER is not created
     * here"* is only actionable if the reader can see which rows the importer thought were
     * partners. On a file with no `level` column every row reads as the surface's default.
     */
    level: importLevelSchema,
    outcome: importOutcomeSchema,
    /**
     * What this row NAMES, for a report a reader can scan.
     *
     * ⚠️ **Generic, because six levels name six different things** — a registration, a licence
     * number, a company name. This was `registration` for the one day the import created only
     * vehicles; carrying a vehicle-specific field name across a driver row would leave the
     * operator's licence numbers with nowhere to render, and a second nullable field per level is
     * how a report grows one column per thing it might have been.
     *
     * NULL when the row names nothing readable — a blank line, or a row so malformed the
     * identifying column could not be read.
     */
    subject: z.string().nullable(),
    /**
     * Why, in words, when the outcome is not `CREATE`.
     *
     * ⚠️ Quotes the offending VALUE, never just the column: *"no vehicle class
     * named 'Van-medium'"* is actionable where *"unknown class"* is a second
     * lookup. Null on a clean row rather than an empty string, so "nothing to say"
     * and "said nothing" stay apart.
     */
    reason: z.string().nullable(),
    /**
     * The other line, when this row duplicates one (A3).
     *
     * ⚠️ Reporting only the row in hand is nearly useless — the operator then has
     * to search the file for its partner, on the error most likely to occur in a
     * hand-built spreadsheet.
     */
    duplicateOfLine: z.number().int().nullable(),
});
export const vehicleImportReportSchema = z.object({
    /**
     * ⚠️ `false` for a VALIDATE and `true` for an APPLY, and the report says which
     * rather than leaving a reader to infer it. "This file is valid" and "this
     * file created 47 vehicles" are different facts, and only the second is
     * something to act on.
     */
    applied: z.boolean(),
    rows: z.array(importRowReportSchema),
    /** Counts per outcome, so a 5,000-row file does not have to be added up by eye. */
    created: z.number().int(),
    skipped: z.number().int(),
    refused: z.number().int(),
    /**
     * How many rows state no drivetrain — A6's WARNING, and it is a warning rather than a
     * refusal on purpose.
     *
     * ⚠️ Blank is UNKNOWN, never an assumed diesel, so such a file is perfectly legal and
     * refusing it would block a customer who simply has not recorded powertrains. But the
     * cohort is keyed on the PAIR (class, drivetrain), so those vehicles compare only against
     * others that also have none — silence here leaves a customer wondering for a month why
     * their EVs have no comparison. A COUNT rather than a per-row reason, beside the three
     * above, because it is a fact about the FILE and a note on 142 rows is noise that buries
     * the rows that actually refused.
     */
    noDrivetrain: z.number().int(),
    /**
     * How many `DRIVER` rows state no licence number — A8's warning, and the same shape as
     * `noDrivetrain`: a fact about the FILE, counted rather than repeated per row.
     *
     * ⚠️ **It is a warning rather than a refusal because the driver dialog accepts it too**, and
     * a UI stricter than its server hides a capability. What it costs is specific and invisible:
     * `(tenantId, licenceNo)` is what makes a re-run skip rather than duplicate (D6), so these
     * rows are the ONLY ones in the file that are not idempotent. An operator who re-uploads a
     * corrected file gets every licence-less driver a second time, and nothing would have told
     * them.
     */
    driversWithoutLicence: z.number().int(),
    /**
     * Per level: how many rows, and what would happen to them.
     *
     * ⚠️ **The combined file is the point, so the totals alone hide the shape of it.** "47
     * created" over a file of partners, companies and vehicles says nothing about whether the
     * depots were made. Keyed by level rather than an array, so a level with no rows is ABSENT
     * rather than a row of zeroes — the report describes the file that was uploaded.
     */
    byLevel: z.record(importLevelSchema, z.object({
        rows: z.number().int(),
        created: z.number().int(),
        skipped: z.number().int(),
        refused: z.number().int(),
    })),
    /**
     * ⚠️ Present on an APPLY that stopped early — the row cap, or an entitlement
     * that was withdrawn mid-run (D13). A partial application reported as a whole
     * one is the silent truncation this platform refuses everywhere: the operator
     * would believe the file was done.
     */
    stoppedAfterLine: z.number().int().nullable(),
});
/**
 * ⚠️ The file is posted as TEXT rather than multipart in v1, and the cap is
 * REFUSED rather than truncated. A file beyond the cap that imported its first
 * 5,000 rows and said so is still a file the operator has to reason about in two
 * halves; refusing it asks them to split it deliberately.
 */
export const vehicleImportRequestSchema = z.object({ csv: z.string().min(1) }).strict();
//# sourceMappingURL=vehicle-import.js.map